import React, { FC, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { CAV6 } from '../cav6tokens';

export type UploadChoiceMode = 'choice' | 'resume';

interface Props {
  open: boolean;
  mode: UploadChoiceMode;
  /** Override copy in `choice` mode (e.g. include ETA in the body). */
  bodyOverride?: string;
  /**
   * Primary CTA fill color. Should be `theme.palette.primary.main` from the
   * host. Without this, the button falls back to the dark-teal default
   * (`#2f4f4f`) which doesn't match the per-account brand color used on
   * other primary CTAs in the flow ("Yes, It's Me!", etc.).
   */
  brandColor?: string;
  /** "Continue upload" — choice mode only. */
  onContinue?: () => void;
  /** "I'll use Wi-Fi later" — choice mode only. */
  onWifiLater?: () => void;
  /**
   * Replaces the mode's default title. The intent-screen resume offer uses
   * this: its default resume title ("Upload paused for Wi-Fi") claims a user
   * action that never happened when the upload was interrupted by a killed
   * tab or connection loss.
   */
  titleOverride?: string;
  /** "Resume upload" — resume mode only. */
  onResume?: () => void;
  /** Dismiss/close handler — used by scrim + Close button. */
  onClose: () => void;
}

const Scrim = styled(Box)<{ $open?: boolean }>(({ $open }) => ({
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.34)',
  opacity: $open ? 1 : 0,
  pointerEvents: $open ? 'auto' : 'none',
  transition: 'opacity 180ms ease',
  zIndex: 3400,
}));

const Sheet = styled(Box)<{ $open?: boolean }>(({ $open }) => ({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 3500,
  background: '#fff',
  borderRadius: '20px 20px 0 0',
  boxShadow: '0 -14px 34px rgba(0,0,0,.18)',
  padding: '16px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
  transform: $open ? 'translateY(0)' : 'translateY(110%)',
  transition: `transform 220ms ${CAV6.easeOut}`,
  fontFamily: CAV6.font,
}));

const Grab = styled('div')({
  width: '42px',
  height: '4px',
  borderRadius: '999px',
  background: 'rgba(0,0,0,.2)',
  margin: '0 auto 12px',
});

const Title = styled('h3')({
  margin: 0,
  textAlign: 'center',
  fontSize: '1.08rem',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'rgba(0,0,0,.86)',
});

const Body = styled('p')({
  margin: '8px auto 0',
  maxWidth: '38ch',
  textAlign: 'center',
  color: 'rgba(0,0,0,.68)',
  fontSize: '0.95rem',
  lineHeight: 1.4,
});

const Actions = styled(Box)({
  marginTop: '14px',
  display: 'grid',
  gap: '10px',
});

const Button = styled('button', {
  shouldForwardProp: (prop) => prop !== '$primary' && prop !== '$brand',
})<{ $primary?: boolean; $brand?: string }>(({ $primary, $brand }) => ({
  minHeight: '48px',
  borderRadius: '12px',
  border: $primary ? 'none' : '1px solid rgba(0,0,0,.14)',
  background: $primary ? $brand || '#2f4f4f' : '#fff',
  color: $primary ? '#fff' : 'rgba(0,0,0,.86)',
  fontWeight: 700,
  fontSize: '1rem',
  fontFamily: CAV6.font,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'opacity 160ms ease, transform 160ms ease',
  '&:active': { transform: 'translateY(1px)' },
}));

const CHOICE_COPY = {
  title: 'This connection may take a while',
  body: 'Continue upload now, or save progress and resume when you are on stronger Wi-Fi.',
};

const RESUME_COPY = {
  title: 'Upload paused for Wi-Fi',
  body: 'Saved on this device for later (when supported). If needed, we may ask you to reselect this video to resume.',
};

/**
 * Bottom-sheet decision surface for slow-network and resume-from-pause UX.
 *
 * Two modes:
 *  - `choice`  — shown when the link reads as collapsing for several chunk
 *    measurements in a row (thresholds and their calibration history live in
 *    `services/upload/slowConnectionPolicy`). One-shot per upload. Offers
 *    "Continue upload" (keep going, and the visually primary action — the
 *    default we want is finishing now) or "I'll use Wi-Fi later" (pause).
 *  - `resume`  — shown when the upload is currently in `paused_wifi`. Offers
 *    "Resume upload" or "Close".
 *
 * Dismiss handlers (scrim, Escape key) call `onClose` regardless of mode.
 */
export const UploadChoiceSheet: FC<Props> = ({
  open,
  mode,
  bodyOverride,
  titleOverride,
  brandColor,
  onContinue,
  onWifiLater,
  onResume,
  onClose,
}) => {
  // Escape-key dismiss
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const copy = mode === 'resume' ? RESUME_COPY : CHOICE_COPY;
  const body = bodyOverride ?? copy.body;

  // The sheet stays mounted and flips to aria-hidden when closed. If the
  // element the user just tapped (Continue / Wi-Fi later / scrim) still holds
  // focus at that moment, Chrome logs an accessibility violation ("aria-hidden
  // on an element whose descendant retained focus") — QA finding #7. Release
  // focus before the hidden state paints.
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (open) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && sheetRef.current?.contains(active)) {
      active.blur();
    }
  }, [open]);

  return (
    <>
      <Scrim $open={open} onClick={onClose} aria-hidden={!open} />
      <Sheet
        ref={sheetRef}
        $open={open}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label="Upload connection options"
      >
        <Grab aria-hidden="true" />
        <Title>{titleOverride ?? copy.title}</Title>
        <Body>{body}</Body>
        <Actions>
          {mode === 'choice' ? (
            <>
              <Button $primary $brand={brandColor} onClick={onContinue}>
                Continue upload
              </Button>
              <Button onClick={onWifiLater}>I&apos;ll use Wi-Fi later</Button>
            </>
          ) : (
            <>
              <Button $primary $brand={brandColor} onClick={onResume}>
                Resume upload
              </Button>
              <Button onClick={onClose}>Close</Button>
            </>
          )}
        </Actions>
      </Sheet>
    </>
  );
};
